import { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigationType } from "react-router-dom";
import { MotionConfig } from "framer-motion";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { LanguageProvider, useLang } from "@/context/LanguageContext";
import { AccessibilityProvider, useA11y } from "@/context/AccessibilityContext";
import AccessibilityWidget from "@/components/AccessibilityWidget";
import SectionListenButtons from "@/components/SectionListenButtons";
import { initLenis, scrollToTop, scrollToPos } from "@/lib/smoothScroll";
import Home from "@/pages/Home";

const Risultati = lazy(() => import("@/pages/Risultati"));
const ArchivioStampa = lazy(() => import("@/pages/ArchivioStampa"));
const ArchivioArticolo = lazy(() => import("@/pages/ArchivioArticolo"));
const VideoGare = lazy(() => import("@/pages/VideoGare"));
const ClassiVisuallyImpaired = lazy(() => import("@/pages/ClassiVisuallyImpaired"));
const Admin = lazy(() => import("@/pages/Admin"));

function ScrollToTop() {
  const location = useLocation();
  const navType = useNavigationType();
  useEffect(() => {
    // ritorno alla home: ripristina la posizione salvata sia col tasto "torna alla home"
    // (state.restore) sia con la freccia indietro del browser o del telefono (POP)
    if (location.pathname === "/" && (location.state?.restore || navType === "POP")) {
      const y = Number(sessionStorage.getItem("gb-home-scroll") || 0);
      const t1 = setTimeout(() => scrollToPos(y), 150);
      const t2 = setTimeout(() => scrollToPos(y), 600);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
    scrollToTop();
  }, [location.pathname]);
  return null;
}

function Shell() {
  const { a11y } = useA11y();
  const { t } = useLang();
  return (
    <MotionConfig reducedMotion={a11y ? "always" : "never"}>
      <a href="#contenuto" className="skip-link">{t("a11y.skip")}</a>
      <BrowserRouter>
        <ScrollToTop />
        <SectionListenButtons />
        <Suspense fallback={<div className="min-h-screen bg-[#090D14]" />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/risultati" element={<Risultati />} />
            <Route path="/archivio-stampa" element={<ArchivioStampa />} />
            <Route path="/archivio-stampa/:id" element={<ArchivioArticolo />} />
            <Route path="/video" element={<VideoGare />} />
            <Route path="/classi-visually-impaired" element={<ClassiVisuallyImpaired />} />
            <Route path="/admin" element={<Admin />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
      <AccessibilityWidget />
    </MotionConfig>
  );
}

function App() {
  useEffect(() => {
    initLenis();
  }, []);

  return (
    <div className="App">
      <AuthProvider>
        <LanguageProvider>
          <AccessibilityProvider>
            <Shell />
          </AccessibilityProvider>
        </LanguageProvider>
        <Toaster theme="dark" position="top-center" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
